import { HttpParams } from '@angular/common/http';
import { HttpParamEncoder } from '../../models/http/http-param-encoder.model';
import { HttpParam } from '../../models/http/http.type';

export function isUrl(url: string): boolean {
  let urlComparison;
  try {
    urlComparison = new URL(url);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return false;
  }
  return (
    urlComparison.protocol === 'http:' || urlComparison.protocol === 'https:'
  );
}

export function formatUrl(url: string): string {
  const protocolRegexp = new RegExp(/(http|https):\/{2}/);
  const protocolMatch = url.match(protocolRegexp);
  const protocol = (protocolMatch && protocolMatch[0]) || '';

  const path = url
    .replace(protocolRegexp, '')
    .replace(/\/{2,}/g, '/')
    .replace(/\/+$/, '');

  return `${protocol}${path}`;
}

export function buildUrl(
  url: string,
  params: Record<string, HttpParam | HttpParam[]> = {},
): string {
  const queryString: string = httpParamsFromObject(params).toString();

  if (queryString) {
    return `${url}?${queryString}`;
  }
  return url;
}

export function httpParamsFromObject(
  obj: Record<string, HttpParam | HttpParam[]>,
): HttpParams {
  const formattedParams: Record<string, string | string[]> = Object.entries(
    obj,
  ).reduce(
    (
      params: Record<string, string | string[]>,
      [key, param]: [string, HttpParam | HttpParam[]],
    ) => {
      const formattedParam = Array.isArray(param)
        ? formatHttpArrayParam(param)
        : formatHttpParam(param);

      if (formattedParam) {
        return {
          ...params,
          [key]: formattedParam,
        };
      }
      return params;
    },
    {},
  );

  return new HttpParams({
    encoder: new HttpParamEncoder(),
    fromObject: formattedParams,
  });
}

function formatHttpParam(param: HttpParam): string | undefined {
  switch (typeof param) {
    case 'string':
      return param;
    case 'number':
      return param.toString();
    case 'boolean':
      return param.toString();
    default:
      return undefined;
  }
}

function formatHttpArrayParam(param: HttpParam[]): string[] {
  return param.reduce((formattedParam: string[], childParam: HttpParam) => {
    const formattedChildParam = formatHttpParam(childParam);

    if (formattedChildParam) {
      return [formattedChildParam, ...formattedParam];
    }
    return formattedParam;
  }, []);
}

export function formatHttpPayload<T>(
  payload: T | undefined | null,
): T | undefined | null {
  if (!(payload instanceof Object) || payload instanceof Array) {
    return payload;
  }

  return Object.entries(payload).reduce(
    (
      formattedPayload: Record<string, unknown>,
      [key, property]: [string, unknown],
    ) => {
      const formattedProperty = formatHttpPayloadProperty(property);

      if (formattedProperty === null || formattedProperty === undefined) {
        return formattedPayload;
      }

      return {
        ...formattedPayload,
        [key]: formattedProperty,
      };
    },
    {},
  ) as unknown as T;
}

function formatHttpPayloadProperty<T>(property: T): T | undefined {
  if (Array.isArray(property)) {
    return property
      .map((property: unknown) => formatHttpPayloadProperty(property))
      .filter(
        (property: unknown) => property !== null && property !== undefined,
      ) as unknown as T;
  } else if (typeof property === 'string') {
    return property === '' ? undefined : property;
  } else if (property === null || property === undefined) {
    return property;
  } else if (typeof property === 'object') {
    return formatHttpPayload(property as Record<string, unknown>) as T;
  }
  return property;
}

export function isDomesticAndGeneralApi(url: string): boolean {
  const domesticAndGeneralDomainPattern = new RegExp(
    /(domgen)|(ao-care)|(domesticandgeneral)/,
  );
  const domesticAndGeneralApiPattern = new RegExp(/(api)/);

  return (
    domesticAndGeneralDomainPattern.test(url) &&
    domesticAndGeneralApiPattern.test(url)
  );
}
